<script setup>
import { useGetAgeLimitString } from '~/core/composables/useGetAgeLimitString'
import { useSessionData } from '@/core/composables/useSessionData'
import { useCalendarSettings } from '@/schedule/composables/useCalendarSettings'
import { useCalendarBus } from '~/schedule/composables/useCalendarBus'
import getEventItemInfo from '~/schedule/utils/getEventItemInfo'

const props = defineProps({
  event: {
    type: Object,
    default: undefined,
  },
})

const { features } = useFeatures()

const { calendarSettings } = useSessionData()

const calendarBus = useCalendarBus()
const { show_age_restriction_filter: showAgeFilter } = useCalendarSettings()

const scheduleItemData = toRef(() => getEventItemInfo(props.event))

const { t, d } = useI18n()
const { convertDateTimeForDisplay } = useTimeZone()
const { getAgeLimitString } = useGetAgeLimitString()

const ageLimitStringParams = computed(() => {
  return {
    min: props.event.offering_type?.min_allowed_age,
    max: props.event.offering_type?.max_allowed_age,
  }
})

const tooltipData = computed(() => {
  const data = []
  if (props.event.is_appointment)
    data.push({ icon: 'i-fas-user-clock', text: t('Appointment') })

  if (props.event.is_online)
    data.push({ icon: 'i-fas-video', text: t('Online event') })

  if (props.event.is_course)
    data.push({ icon: 'i-fas-book', text: t('This is part of a course') })

  return data
})

function selectEvent() {
  !eventUrl.value
  && calendarBus.emit('calendar-event-selected', props.event?.id)
}

const hasAgeRestrictions = computed(() =>
  props.event.offering_type?.min_allowed_age || props.event.offering_type?.max_allowed_age,
)

const eventUrl = computed(() =>
  props.event.is_course ? props.event.customer_url : undefined,
)

const isAppointmentForFamilyMemberRegistrationStatus = computed(() => (
  props.event.registrationsClosed
  && props.event.registrationsNotOpen
  && !props.event.active_registration_status?.registrations_close_at
  && !props.event.active_registration_status?.registrations_open_at),
)
</script>

<template>
  <div
    class="calendar-event"
    :style="
      !event.isEnded ? [scheduleItemData.backgroundColorStyle, scheduleItemData.ringColorStyle] : []
    "
    :class="{
      'expired': event.isEnded,
      'registrations-closed': event.registrationsClosed,
      'registrations-not-open': event.registrationsNotOpen,
    }"
  >
    <div
      class="flex flex-wrap items-center justify-between gap-4 rounded-8px p-4 text-left text-12px text-text-500 font-400 font-sans ring-1 ring-inset"
      :style="!event.isEnded ? [scheduleItemData.ringColorStyle] : []"
    >
      <div
        class="avatars"
        :class="{ 'filter-grayscale': event.isEnded }"
      >
        <DSAvatarList
          :dual-avatar="true"
          :people="
            event.instructors.map(instructor => ({
              ...instructor,
              imageUrl: instructor.picture_url,
            }))
          "
          :max="2"
          border
          :size="event.instructors.length > 1 ? 'md' : 'lg'"
        />
      </div>
      <div class="flex flex-grow-1 flex-col gap-2 overflow-hidden">
        <div class="eventitem-name">
          <h6
            class="title flex flex-row items-center text-14px text-text-600 font-600"
          >
            <span
              v-if="tooltipData.length > 0"
              class="flex"
            >
              <DSTooltip
                v-for="tooltip in tooltipData"
                :key="tooltip.icon"
                variant="dark"
                placement="top"
                other-classes="px-4 py-2"
              >
                <DSIcon :icon="tooltip.icon" />
                <template #content>
                  <p class="text-xs">
                    {{ tooltip.text }}
                  </p>
                </template>
              </DSTooltip>
            </span>
            {{ event.name }}
          </h6>
        </div>

        <p
          v-if="event.customer_attending"
          class="registered"
        >
          <span class="inline-flex gap-2 rounded-2px bg-secondary-400 px-2 py-2px text-white">
            <span>
              {{ t('Registered') }}
            </span>
            <DSIcon icon="i-fas-check" />
          </span>
        </p>

        <div v-if="event.instructors">
          <ul class="instructors">
            <template
              v-for="(item, itemIndex) in event.instructors"
              :key="itemIndex"
            >
              <li>{{ item.name }}</li>
            </template>
          </ul>
          <p class="location">
            {{ event.venue?.name }}
          </p>
        </div>
        <div v-if="event.category">
          <p class="text-xs fw-600 uppercase">
            {{ event.category }}
          </p>
        </div>

        <div class="flex flex-col gap-10px">
          <template v-if="hasAgeRestrictions && features.age_restrictions && showAgeFilter">
            <p class="flex items-center">
              <DSIcon icon="i-fas-hand" />
              <span>{{ getAgeLimitString(ageLimitStringParams) }}</span>
            </p>
          </template>
          <template
            v-if="isAppointmentForFamilyMemberRegistrationStatus"
          >
            <p class="flex gap-1">
              <DSIcon
                size="12"
                icon="i-fas-users"
              />
              {{ t('Full') }}
            </p>
          </template>

          <template v-else-if="!event.registrationsClosed && !event.registrationsNotOpen">
            <p v-if="calendarSettings.show_attending_count">
              <template v-if="event.is_full">
                <DSIcon
                  size="12"
                  icon="i-fas-users"
                />
                {{ t('Full') }} {{ event.attendingText }}
              </template>
              <template v-else>
                <DSIcon
                  size="12"
                  icon="i-fas-user"
                />
                {{ event.attendingText }}
              </template>
            </p>

            <template v-else-if="scheduleItemData.eventHasWaitlist">
              <!-- If customer is on waitlist -->
              <template v-if="event.customer_on_waitlist">
                <span
                  class="w-fit rounded-4 bg-schedule-yellow px-2 py-2px text-text-500 fw-600"
                >
                  {{ t('Waitlist') }}<template v-if="calendarSettings.show_waitlist_positions">: {{ props.event.waiting_count }} <template v-if="+props.event.waitlist_max > 0"> / {{ props.event.waitlist_max }}</template></template>
                </span>
              </template>
              <!-- general # of waitlist -->
              <template
                v-else-if="calendarSettings.show_waitlist_positions && (props.event.is_full || props.event.waiting_count > 0)"
              >
                <p class="flex gap-1 text-text-500">
                  <DSIcon
                    size="12"
                    icon="i-fas-clock"
                  /> {{ t("Waitlist") }} {{ event.waiting_count }}
                </p>
              </template>
            </template>
          </template>

          <template v-else>
            <p class="flex gap-1 text-text-500">
              <DSIcon
                size="12"
                icon="i-fas-lock"
              />
              <span v-if="event.registrationsNotOpen">
                <i18n-t keypath="Registration opens {openDate}">
                  <template #openDate>
                    <time
                      class="ml-2px"
                      :datetime="
                        event.active_registration_status
                          ?.registrations_open_at
                      "
                    >
                      {{
                        d(convertDateTimeForDisplay(event.active_registration_status?.registrations_open_at), 'long')
                      }}
                    </time>
                  </template>
                </i18n-t>
              </span>
              <span v-else>
                <i18n-t keypath="Registration closed {closedDate}">
                  <template #closedDate>
                    <time
                      class="ml-2px"
                      :datetime="
                        event.active_registration_status
                          ?.registrations_close_at
                      "
                    >
                      {{
                        d(convertDateTimeForDisplay(event.active_registration_status?.registrations_close_at), 'long')
                      }}
                    </time>
                  </template>
                </i18n-t>
              </span>
            </p>
          </template>
        </div>
      </div>
      <div class="cta lt-md:w-full">
        <DSButton
          v-if="!event.is_course"
          class="btn-md min-w-118px uppercase btn-fill-primary"
          @click="selectEvent"
        >
          <template v-if="event.isEnded">
            {{ t('Ended') }}
          </template>
          <template v-else-if="event.is_appointment">
            {{ t('View Appointment') }}
          </template>
          <!-- active_registration_status.current_status -->
          <template v-else-if="event.active_registration_status">
            <template v-if="event.active_registration_status.current_status === 'registered'">
              {{ t('Details') }}
            </template>
            <template v-else-if="event.active_registration_status.current_status === 'on_waitlist'">
              {{ t('On Waitlist') }}
            </template>
            <!-- active_registration_status.suggested_action -->
            <!-- 'register' accounts for current_status === 'spot_reserved' -->
            <template v-else-if="['register', 'sign_in'].includes(event.active_registration_status.suggested_action)">
              {{ t('Book') }}
            </template>
            <template v-else-if="event.active_registration_status.suggested_action === 'join_waitlist'">
              {{ t('Join Waitlist') }}
            </template>
            <!-- active_registration_status.restriction_codes -->
            <template v-else-if="event.active_registration_status.restriction_codes.includes('event_full')">
              {{ t('Full') }}
            </template>
            <template
              v-else-if="event.active_registration_status.restriction_codes.some(code => ['registrations_closed', 'registrations_not_open'].includes(code))"
            >
              {{ t('Details') }}
            </template>
            <template v-else>
              {{ t('Details') }}
            </template>
          </template>
          <template v-else>
            {{ t('Details') }}
          </template>
        </DSButton>

        <BaseLink
          v-else
          :url="eventUrl"
          :v2-link="false"
          :apply-current-provider-prefix="false"
        >
          <DSButton class="btn-md min-w-118px uppercase btn-fill-primary">
            {{ t('View Course') }}
          </DSButton>
        </BaseLink>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.calendar-event {
  @apply hover:(ring-2 shadow-[0_2px_20px_rgba(45,46,52,.25)]) rounded-8px w-full;

  &.cancelled {
    .title {
      @apply decoration-line-through;
    }
  }

  &.expired {
    @apply border-surface-700;
    .title,
    .time-range,
    [data-ds-component='ds-icon'],
    .instructors li,
    .registered span,
    .location,
    .registered span [data-ds-component='ds-icon'],
    p {
      @apply text-text-200;
    }

    .registered span {
      @apply bg-surface-600;
    }
  }

  &.registrations-not-open,
  &.registrations-closed {
    .title,
    .title [data-ds-component='ds-icon'],
    .time-range,
    .location,
    .instructors li {
      @apply text-text-400;
    }
  }

  .instructors li,
  .location {
    @apply text-text-400;
  }

  .registered {
    [data-ds-component='ds-icon'] {
      @apply text-white;
    }
  }

  .title,
  .time-range,
  .instructors li {
    @apply overflow-hidden whitespace-nowrap;
  }

  [data-ds-component='ds-icon'] {
    @apply m-inline-end-1 text-secondary-500;
  }
}
</style>

<i18n>
{
  "en-us": {
    "Book": "Book",
    "On Waitlist": "On Waitlist",
    "Details": "Details",
    "Join Waitlist": "Join Waitlist",
    "Full": "Full",
    "Registered": "Registered",
    "Waitlist": "Waitlist",
    "Online event": "Online event",
    "Appointment": "Appointment",
    "Registration opens {openDate}": "Registration opens {openDate}",
    "Cancelled": "Cancelled",
    "This is part of a course": "This is part of a course",
    "Ended": "Ended",
    "View Course": "View Course",
    "View Appointment": "View Appointment",
    "Registration closed {closedDate}": "Registration closed {closedDate}"
  }
}
</i18n>

<i18n locale="de" lang="json">
{
  "Book": "Buchen",
  "On Waitlist": "Auf der Warteliste",
  "Details": "Details",
  "Join Waitlist": "Warteliste beitreten",
  "Full": "Belegt",
  "Registered": "Registriert",
  "Waitlist": "Warteliste",
  "Online event": "Online-Veranstaltung",
  "Appointment": "Termin",
  "Registration opens {openDate}": "Registrierung öffnet am {openDate}",
  "Cancelled": "Storniert",
  "This is part of a course": "Dies ist Teil eines Kurses",
  "Ended": "Beendet",
  "View Course": "Kurs anzeigen",
  "View Appointment": "Termin anzeigen",
  "Registration closed {closedDate}": "Registrierung geschlossen am {closedDate}"
}
</i18n>

<i18n locale="fr" lang="json">
{
  "Book": "Réserver",
  "On Waitlist": "Sur la liste d'attente",
  "Details": "Détails",
  "Join Waitlist": "Rejoindre la liste d'attente",
  "Full": "Plein",
  "Registered": "Inscrit",
  "Waitlist": "Liste d'attente",
  "Online event": "Événement en ligne",
  "Appointment": "Rendez-vous",
  "Registration opens {openDate}": "Les inscriptions ouvriront le {openDate}",
  "Cancelled": "Annulé",
  "This is part of a course": "Cela fait partie d'un stage",
  "Ended": "Terminé",
  "View Course": "Voir le stage",
  "View Appointment": "Voir le rendez-vous",
  "Registration closed {closedDate}": "Inscriptions clôturées le {closedDate}"
}
</i18n>

<i18n locale="es" lang="json">
{
  "Book": "Reservar",
  "On Waitlist": "En lista de espera",
  "Details": "Detalles",
  "Join Waitlist": "Entrar en la lista de espera",
  "Full": "Completo",
  "Registered": "Se ha inscrito",
  "Waitlist": "Lista de espera",
  "Online event": "Evento online",
  "Appointment": "Cita",
  "Registration opens {openDate}": "La inscripción empieza el día {openDate}",
  "Cancelled": "Se ha cancelado",
  "This is part of a course": "Esto es parte de un curso",
  "Ended": "Ha finalizado",
  "View Course": "Ver curso",
  "View Appointment": "Ver cita",
  "Registration closed {closedDate}": "Registro cerrado {closedDate}"
}
</i18n>
